import axios from 'axios'
import { produce } from 'immer'
import _, { get } from 'lodash'
import Papa from 'papaparse'
import { GROUPING_LIST, METRIC_DEFAULT_MULTIPLIER, PILLAR_INFO, PillarType, SWData } from '../constant'
import {
  ArrElement,
  BarChartGlobalData,
  BarChartNationalData,
  BarChartNationalRankedData,
  Country,
  DataKey,
  MetricGroup,
  MetricValue,
  SlopeData,
} from '../types'
import { modifyMetricsInGroup } from './modifyMetrics'
import { getDataAgainstMetricKey, simplifyMetricGroup } from './tableViewUtils'
import { transformMetricGroups } from './transformMetricGroups'

export const isDataKeyExistsInData = (dataParam: any[] | undefined, pillarInfo: ArrElement<typeof PILLAR_INFO>) => {
  if (!dataParam) return
  return dataParam.some((dataEl) => {
    return Object.keys(dataEl).some((key) => {
      if (pillarInfo.dataKey === key) return true
      return false
    })
  })
}

export const isEmptyData = (dataParam: BarChartGlobalData) =>
  !dataParam || dataParam.length === 0 || !dataParam.some((dataEl) => dataEl.score !== 0)

export const arrayOfPillarValue = (metricGroups: MetricGroup[]) =>
  metricGroups.map((metricGroupEl) => metricGroupEl.multiplier)

export const arrayOfMetricValue = (metricGroups: MetricGroup[], metricValue: MetricValue) =>
  metricGroups.flatMap((metricGroupEl) =>
    metricGroupEl.metrics.filter((metric) => metric.metricValue === metricValue).map((metric) => metric.multiplier),
  )

export const isAllPillarValueEmpty = (metricGroups: MetricGroup[]) =>
  !arrayOfPillarValue(metricGroups).some((value) => value !== 0)

export const isAllMetricValueEmpty = (metricGroups: MetricGroup[], metricValue: MetricValue) =>
  !arrayOfMetricValue(metricGroups, metricValue).some((value) => value !== 0)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loadCsv = async (filePath: string): Promise<{ name: string; data: Record<string, string>[] }> => {
  const response = await axios.get(filePath, { responseType: 'blob' })
  return new Promise((resolve, reject) => {
    Papa.parse(response.data, {
      header: true,
      complete: (results) => {
        resolve({
          name: 'data',
          data: results.data as Record<string, string>[],
        })
      },
      error: (error) => reject(error),
    })
  })
}

export const createCountryData = async (
  filePath: string,
): Promise<{ fullCountryData: Country[]; countryData: Country[] }> => {
  const rawData = await loadCsv(filePath)
  const allData = rawData.data
  const coreData = rawData.data.filter((eachData) => eachData.CountryGroup === 'core')
  const fullCountryDataObj = _.groupBy(allData, 'CountryName')
  const countryDataObj = _.groupBy(coreData, 'CountryName')
  const fullCountryData: Country[] = Object.entries(fullCountryDataObj).map(([countryName, metadata]) => {
    return {
      countryName,
      countryCode: metadata[0].Country,
      region: metadata[0].Region,
      incomeGroup: metadata[0].IncomeGroup,
      countryLongCode: metadata[0].Code,
      isAdhocCountry: metadata[0].CountryGroup === 'extra',
      metadatas: metadata.map((el) => {
        return Object.entries(el).reduce(
          (prev, [currKey, currValue]) => {
            if (!_.isNaN(Number(currValue))) {
              prev[currKey] = parseFloat(currValue)
            } else {
              prev[currKey] = currValue
            }
            return prev
          },
          {} as Record<string, string | number>,
        )
      }),
    }
  }) as Country[]
  const countryData: Country[] = Object.entries(countryDataObj).map(([countryName, metadata]) => ({
    countryName,
    countryCode: metadata[0].Country,
    region: metadata[0].Region,
    incomeGroup: metadata[0].IncomeGroup,
    countryLongCode: metadata[0].Code,
    metadatas: metadata.map((el) => {
      return Object.entries(el).reduce(
        (prev, [currKey, currValue]) => {
          if (!_.isNaN(Number(currValue))) {
            prev[currKey] = parseFloat(currValue)
          } else {
            prev[currKey] = currValue
          }
          return prev
        },
        {} as Record<string, string | number>,
      )
    }),
  })) as Country[]
  const filteredFullCountryData = fullCountryData.filter((i) => i.countryCode && i.countryLongCode)
  const filteredData = countryData.filter((i) => i.countryCode && i.countryLongCode)
  return { fullCountryData: filteredFullCountryData, countryData: filteredData }
}

export const createMetricData = async (filePath: string): Promise<MetricGroup[]> => {
  const rawData = await loadCsv(filePath)
  const metricGroupDataObj = _.groupBy(rawData.data, 'pillar_1')
  const metricGroupDataObjFilter = Object.entries(metricGroupDataObj).filter((el) => el[0] !== 'undefined')

  // const randomizeMultiplierValue = generateNaturalArray(metricGroupDataObjFilter.length, 100)
  const metricGroupData = metricGroupDataObjFilter.map(([metricGroupName, metrics]) => {
    const findPillar = PILLAR_INFO.find((pillar) => pillar.name === metricGroupName)
    return {
      metricGroupName,
      metricGroupNameDatakey: findPillar!.dataKey,
      metrics: metrics.map((metric) => {
        const refinedName = metric.metric_name?.replace(' PC', '')?.trim()
        const mtp = METRIC_DEFAULT_MULTIPLIER?.[refinedName as keyof typeof METRIC_DEFAULT_MULTIPLIER]
        return {
          ...metric,
          metricName: metric.metric_name,
          metricValue: (!metric.metric_name.includes(' PC') ? 'absolute' : 'perCapita') as 'absolute' | 'perCapita',
          multiplier: mtp / 10,
          is_for_ranking: metric?.is_for_ranking || 'no',
        } as { [key: string]: string | number } & {
          metricName: string
          metricValue: 'absolute' | 'perCapita'
          multiplier: number
          is_for_ranking: 'yes' | 'no'
        }
      }),
      multiplier: findPillar!.defaultMultiplier,
    }
  })
  const transformedMetricGroups = transformMetricGroups(metricGroupData)
  const modifyMetricGroup = modifyMetricsInGroup(transformedMetricGroups)
  return modifyMetricGroup
}

export const createLegendData = async (filePath: string): Promise<Record<string, any>> => {
  const rawData = await loadCsv(filePath)

  const legendMap: Record<string, any> = {}
  rawData?.data?.forEach(({ country, status, year }: any) => {
    if (!legendMap?.[country]) {
      legendMap[country] = {
        [year]: status,
      }
    } else {
      legendMap[country] = {
        ...legendMap[country],
        [year]: status,
      }
    }
  })
  return legendMap
}
export const createLegendYearData = async (filePath: string): Promise<Record<string, any>> => {
  const rawData = await loadCsv(filePath)
  const legendYearMap: Record<string, any> = {}
  rawData?.data?.forEach(({ country, _status, year_released }: any) => {
    legendYearMap[country] = year_released
  })
  return legendYearMap
}

export const createMetricDataTypeMap = async (filePath: string): Promise<Record<string, string>> => {
  const rawData = await loadCsv(filePath)
  const map: Record<string, string> = {}
  rawData?.data?.forEach((row: any) => {
    if (row.data_type) {
      map[row.column_name] = row.data_type
    }
  })
  return map
}
export const getCountryCodeFromCountryLongCode = (countryData: Country[], countryLongCode: string) =>
  countryData.find((countryEl) => countryEl.countryLongCode === countryLongCode)?.countryCode

export const getCountryLongCodeFromCountryCode = (countryData: Country[], countryCode: string) =>
  countryData.find((countryEl) => countryEl.countryCode === countryCode)?.countryLongCode

export const getCountryNameFromCountryLongCode = (countryData: Country[], countryLongCode: string) =>
  countryData.find((countryEl) => countryEl.countryLongCode === countryLongCode)?.countryName

export const filterRankingMetrics = (jsonData: MetricGroup[]): MetricGroup[] => {
  try {
    // Deep clone and filter the data
    const filteredData = jsonData.map((group) => ({
      ...group,
      metrics: group.metrics.filter((metric: any) => metric.is_for_ranking === 'yes'),
    }))

    // Convert back to JSON string
    return filteredData
  } catch (error) {
    console.error('Error parsing or filtering JSON:', error)
    return jsonData // return original if something goes wrong
  }
}

export const calculateRangePublishYear = (countryData: Country[]): number[] =>
  countryData
    .reduce((prev, curr) => {
      curr.metadatas.forEach((metadata) => {
        if (!prev.includes(metadata.PublishYear as number)) {
          prev.push(metadata.PublishYear as number)
        }
      })
      return prev
    }, [] as number[])
    .filter((el) => !Number.isNaN(el))
    .sort((a, b) => a - b)

// export const getAllGrouping = (countryData: Country[]): string[] =>
//   countryData
//     .reduce((prev, curr) => {
//       curr.metadatas.forEach((metadata) => {
//         if (!prev.includes(metadata.Region as string)) {
//           prev.push(metadata.Region as string)
//         }
//       })
//       return prev
//     }, [] as string[])
//     .filter((el) => !!el)
//     .sort()

export const calculateCountryDataFromMetricValue4SlopeChart = (
  metricGroupsParam: MetricGroup[],
  countriesParam: Country[],
  configuration: {
    globalYear: number
    metricEndpointWeight: string
    metricEndpointRank: string
    metricValue: MetricValue
    rankingLogic?: {
      metricRanking?: boolean
      pillarRanking?: boolean
      yearRankingEachCountry?: boolean
    }
  },
): Country[] => {
  const nextState = produce(
    { metricGroups: metricGroupsParam, countries: countriesParam },
    ({ countries, metricGroups }) => {
      const {
        globalYear,
        metricEndpointWeight,
        metricEndpointRank,
        metricValue,
        rankingLogic: { metricRanking = true, pillarRanking = true, yearRankingEachCountry = true } = {
          metricRanking: true,
          pillarRanking: true,
          yearRankingEachCountry: true,
        },
      } = configuration

      metricGroups.forEach((metricGroup) => {
        // this map will store all scaled value of each country to calculate data
        const calculatedValues = new Map()

        metricGroup.metrics.forEach((metric) => {
          metricRanking &&
            countries.sort((a, b) => {
              const firstComparableValue = a.metadatas.find((metadata) => metadata.PublishYear === globalYear)?.[
                metric.shortname_scaled
              ] as number

              const secondComparableValue = b.metadatas.find((metadata) => metadata.PublishYear === globalYear)?.[
                metric.shortname_scaled
              ] as number

              return secondComparableValue - firstComparableValue
            })

          countries.forEach((country, index) => {
            // only get metadata of country that has the same year with the filter
            const matchingYearCountryData =
              country.metadatas.find((metadata) => metadata.PublishYear === globalYear) || {}

            matchingYearCountryData[`${metric.shortname_scaled}${metricEndpointRank}`] = index + 1
            // i.e key: "number_of_total_journal_publications" => "number_of_total_journal_publications_weight" with metricEndpoint = 'weight'
            const key = (metric.shortname_raw as string).concat(metricEndpointWeight)
            const value =
              (matchingYearCountryData[metric.shortname_raw] as number) * metric.multiplier * metricGroup.multiplier

            const scaledKey = (metric.shortname_scaled as string).concat(metricEndpointWeight)
            const scaledValue =
              (matchingYearCountryData[metric.shortname_scaled] as number) * metric.multiplier * metricGroup.multiplier

            matchingYearCountryData[key] = value
            matchingYearCountryData[scaledKey] = scaledValue

            if (
              matchingYearCountryData[metric.shortname_scaled] &&
              matchingYearCountryData[metric.shortname_scaled] !== '-' &&
              metric.metricValue === metricValue
            ) {
              calculatedValues.has(country.countryName)
                ? calculatedValues.get(country.countryName).push(scaledValue)
                : calculatedValues.set(country.countryName, [scaledValue])
            }

            GROUPING_LIST.filter((grouping) => grouping.name !== 'pillar').forEach((grouping) => {
              country[grouping.name] = matchingYearCountryData[grouping.headerKey]
            })
          })
        })

        metricGroup.calculatedValues = calculatedValues
      })

      countries.forEach((country) => {
        const totalMetricUsed = metricGroups.reduce(
          (prev, curr) => (curr.calculatedValues?.get(country.countryName)?.length || 0) + prev,
          0,
        )

        metricGroups.forEach((metricGroup) => {
          const seriesName = metricGroup.metricGroupNameDatakey.concat('Total')

          // calculate sum of all scaled value of each country
          country[seriesName] = _.sum(
            metricGroup.calculatedValues
              ?.get(country.countryName)
              ?.filter((number: number | string) => !_.isNaN(number)) || [],
          )

          country.totalMetricUsed = totalMetricUsed
          // Old static implementation
          // country.totalAverage =
          //   // ((country.economyTotal as number) + (country.researchTotal as number)) / totalMetricUsed
          //   (country.economyTotal as number) + (country.researchTotal as number)

          // country.economyShare = (country.economyTotal as number) / totalMetricUsed || 0
          // country.researchShare = (country.researchTotal as number) / totalMetricUsed || 0
          // country.shareSum = (country.economyShare as number) + (country.researchShare as number)

          // Start new implementation
          let total = 0
          let shareNum = 0

          PILLAR_INFO.forEach((p: PillarType) => {
            // total += (country.economyTotal as number)
            total += (country?.[`${p.dataKey}Total`] || 0) as number
            const pillarShare = (country[`${p.dataKey}Total`] as number) / totalMetricUsed || 0
            country[`${p.dataKey}Share`] = pillarShare
            shareNum += pillarShare
          })
          const totalAverage = total / totalMetricUsed

          country.totalAverage = totalAverage
          country.shareSum = shareNum
          // End new implementation
        })
      })

      // calculate ranking for each pillar
      pillarRanking &&
        PILLAR_INFO.forEach((pillar) => {
          countries
            .sort((a, b) => (b[`${pillar.dataKey}Total`] as number) - (a[`${pillar.dataKey}Total`] as number))
            .forEach((country, index) => {
              country[`${pillar.dataKey}ShareRanking`] = index + 1
            })
        })

      // calculate ranking all pillar for the current year
      yearRankingEachCountry &&
        countries
          .sort((a, b) => (b.totalAverage as number) - (a.totalAverage as number))
          .forEach((country, index) => {
            country.ranking = index + 1
          })
    },
  )

  return nextState.countries
}
// TODO: get economyTotal
export const calculateCountryDataFromMetricValue = (
  metricGroupsParam: MetricGroup[],
  countriesParam: Country[],
  configuration: {
    globalYear: number
    metricEndpointWeight: string
    metricEndpointRank: string
    metricValue: MetricValue
    rankingLogic?: {
      metricRanking?: boolean
      pillarRanking?: boolean
      yearRankingEachCountry?: boolean
    }
  },
): Country[] => {
  const filteredMetricGroups = filterRankingMetrics(metricGroupsParam)
  const nextState = produce(
    { metricGroups: filteredMetricGroups, countries: countriesParam },
    ({ countries, metricGroups }) => {
      const {
        globalYear,
        metricEndpointWeight,
        metricEndpointRank,
        metricValue,
        rankingLogic: { metricRanking = true, pillarRanking = true, yearRankingEachCountry = true } = {
          metricRanking: true,
          pillarRanking: true,
          yearRankingEachCountry: true,
        },
      } = configuration

      metricGroups.forEach((metricGroup) => {
        // this map will store all scaled value of each country to calculate data
        const calculatedValues = new Map()

        metricGroup.metrics.forEach((metric) => {
          metricRanking &&
            countries.sort((a, b) => {
              const firstComparableValue = a.metadatas.find((metadata) => metadata.PublishYear === globalYear)?.[
                metric.shortname_scaled
              ] as number

              const secondComparableValue = b.metadatas.find((metadata) => metadata.PublishYear === globalYear)?.[
                metric.shortname_scaled
              ] as number

              return secondComparableValue - firstComparableValue
            })

          countries.forEach((country, index) => {
            // only get metadata of country that has the same year with the filter
            const matchingYearCountryData =
              country.metadatas.find((metadata) => metadata.PublishYear === globalYear) || {}
            const metric_short_name_scaled = `${metric.shortname_scaled}_display`

            matchingYearCountryData[`${metric.shortname_scaled}${metricEndpointRank}`] = index + 1
            // i.e key: "number_of_total_journal_publications" => "number_of_total_journal_publications_weight" with metricEndpoint = 'weight'
            const key = (metric.shortname_raw as string).concat(metricEndpointWeight)

            const value =
              (matchingYearCountryData[metric.shortname_raw] as number) * metric.multiplier * metricGroup.multiplier

            // const scaledKey = (metric.shortname_scaled as string).concat(metricEndpointWeight)
            const scaledKey = (metric_short_name_scaled as string).concat(metricEndpointWeight)
            const scaledValue =
              // (matchingYearCountryData[metric.shortname_scaled] as number) * metric.multiplier * metricGroup.multiplier
              (matchingYearCountryData[metric_short_name_scaled] as number) * metric.multiplier * metricGroup.multiplier

            matchingYearCountryData[key] = value
            matchingYearCountryData[scaledKey] = scaledValue

            if (
              // matchingYearCountryData[metric.shortname_scaled] &&
              // matchingYearCountryData[metric.shortname_scaled] !== '-' &&
              matchingYearCountryData[metric_short_name_scaled] &&
              matchingYearCountryData[metric_short_name_scaled] !== '-' &&
              metric.metricValue === metricValue
            ) {
              calculatedValues.has(country.countryName)
                ? calculatedValues.get(country.countryName).push(scaledValue)
                : calculatedValues.set(country.countryName, [scaledValue])
            }

            GROUPING_LIST.filter((grouping) => grouping.name !== 'pillar').forEach((grouping) => {
              country[grouping.name] = matchingYearCountryData[grouping.headerKey]
            })
          })
        })

        metricGroup.calculatedValues = calculatedValues
      })

      countries.forEach((country) => {
        const totalMetricUsed = metricGroups.reduce(
          (prev, curr) => (curr.calculatedValues?.get(country.countryName)?.length || 0) + prev,
          0,
        )

        metricGroups.forEach((metricGroup) => {
          const seriesName = metricGroup.metricGroupNameDatakey.concat('Total')

          // calculate sum of all scaled value of each country
          country[seriesName] = _.sum(
            metricGroup.calculatedValues
              ?.get(country.countryName)
              ?.filter((number: number | string) => !_.isNaN(number)) || [],
          )

          country.totalMetricUsed = totalMetricUsed
          // TODO: make dynamic
          // country.totalAverage =
          //   ((country.economyTotal as number) + (country.researchTotal as number)) / totalMetricUsed

          // country.economyShare = (country.economyTotal as number) / totalMetricUsed || 0
          // country.researchShare = (country.researchTotal as number) / totalMetricUsed || 0
          // country.shareSum = (country.economyShare as number) + (country.researchShare as number)

          let total = 0
          let shareNum = 0

          PILLAR_INFO.forEach((p: PillarType) => {
            total += (country?.[`${p.dataKey}Total`] || 0) as number
            const pillarShare = (country[`${p.dataKey}Total`] as number) / totalMetricUsed || 0
            country[`${p.dataKey}Share`] = pillarShare
            shareNum += pillarShare
          })
          const totalAverage = total / totalMetricUsed

          country.totalAverage = totalAverage
          country.shareSum = shareNum
        })
      })

      let prevWeight: number | null = null
      let currentRank = 1

      // calculate ranking for each pillar
      pillarRanking &&
        PILLAR_INFO.forEach((pillar) => {
          countries
            .sort((a, b) => (b[`${pillar.dataKey}Weight`] as number) - (a[`${pillar.dataKey}Weight`] as number))
            .forEach((country, index) => {
              if (prevWeight === null || (country[`${pillar.dataKey}Weight`] as number) !== prevWeight) {
                // Implementation of competitive ranking
                currentRank = index + 1
              }
              country[`${pillar.dataKey}ShareRanking`] = currentRank
              prevWeight = country[`${pillar.dataKey}Weight`] as number
              // country[`${pillar.dataKey}ShareRanking`] = index + 1
            })

          if (
            countries[countries.length - 1][`${pillar.dataKey}ShareRanking`] ===
            countries[countries.length - 2][`${pillar.dataKey}ShareRanking`]
          ) {
            countries.forEach((i) => {
              if (
                i[`${pillar.dataKey}ShareRanking`] === countries[countries.length - 1][`${pillar.dataKey}ShareRanking`]
              ) {
                i[`${pillar.dataKey}ShareRanking`] = countries.length
              }
            })
          }
        })

      // calculate ranking all pillar for the current year
      yearRankingEachCountry &&
        countries
          .sort((a, b) => (b.totalScore as number) - (a.totalScore as number))
          .forEach((country, index) => {
            country.ranking = index + 1
          })
    },
  )

  return nextState.countries
}

export const calculateWeightedGlobalBarChart = (
  metricGroups: MetricGroup[],
  selectedMetricValue: MetricValue,
  countriesData: Country[],
  currentYear: number,
): Country[] => {
  return countriesData.map((country) => {
    const dataForCurrentYear = country.metadatas.find((m) => m?.PublishYear === currentYear)
    const filteredMetricGroups = filterRankingMetrics(metricGroups);
    const metricsData = filteredMetricGroups.map((group) => {
      const simplifiedMetrics = simplifyMetricGroup(group, selectedMetricValue)
      const totalMultiplier = simplifiedMetrics.reduce((sum, metric) => {
        const value = get(dataForCurrentYear, `${metric.metricShortNameScaled}_calc`)
        return sum + (isNaN(Number(value)) ? 0 : metric.multiplier)
      }, 0)

      const enhancedMetrics = simplifiedMetrics.map((metric) => {
        const metricScoreData = getDataAgainstMetricKey(country.countryCode, countriesData, {
          // shortnameScaled: metric.metricShortNameScaled as string,
          shortnameScaled: `${metric.metricShortNameScaled}_calc` as string,
          shortNameRaw: metric.metricShortName as string,
          currentSelectedYear: currentYear,
          metricType: selectedMetricValue,
        })
        return {
          ...metric,
          score: metricScoreData.score,
          weightScore: (metric.multiplier / totalMultiplier) * metricScoreData.score,
        }
      })
      const totalWeightScore = enhancedMetrics.reduce((total, metric) => {
        return total + (isNaN(metric.weightScore) ? 0 : metric.weightScore)
      }, 0)

      return {
        ...group,
        metrics: enhancedMetrics,
        totalWeightScore,
      }
    })
    const totalMetricMultiplier = metricsData.reduce((sum, metric) => sum + metric.multiplier, 0)

    // Directly calculate researchWeight and economyWeight using rateMultiplier
    // TODO: dynamic
    // const researchGroup = metricsData.find((group) => group.metricGroupNameDatakey === 'research')
    // const economyGroup = metricsData.find((group) => group.metricGroupNameDatakey === 'economy')

    // const researchWeight = researchGroup
    //   ? researchGroup.totalWeightScore * (researchGroup.multiplier / totalMetricMultiplier)
    //   : 0
    // const economyWeight = economyGroup
    //   ? economyGroup.totalWeightScore * (economyGroup.multiplier / totalMetricMultiplier)
    //   : 0
    // const totalScore = researchWeight + economyWeight
    let totalScore = 0

    const pillarWeights: { [key: string]: number } = {}

    PILLAR_INFO.forEach((pillar: PillarType) => {
      const groupByPillar = metricsData.find((group) => group.metricGroupNameDatakey === pillar.dataKey)
      const pillarWeight = groupByPillar
        ? groupByPillar.totalWeightScore * (groupByPillar.multiplier / totalMetricMultiplier)
        : 0
      pillarWeights[`${pillar.dataKey}Weight`] = isNaN(pillarWeight) ? 0 : pillarWeight
      totalScore += isNaN(pillarWeight) ? 0 : pillarWeight
    })

    return {
      ...country,
      metricsData,
      ...pillarWeights,
      totalScore,
    }
  })
}

export const generateGlobalBarChartData = (
  metricGroupsParam: MetricGroup[],
  countriesData: Country[],
  configuration: {
    globalYear: number
    metricEndpointWeight: string
    metricEndpointRank: string
    metricValue: MetricValue
  },
): BarChartGlobalData => {
  const data = calculateCountryDataFromMetricValue(metricGroupsParam, countriesData, configuration)
  return data
    .map((el) => {
      const pillarValues: Record<DataKey, number> = PILLAR_INFO.reduce(
        (prev, curr: PillarType) => {
          const temp: any = { ...prev }
          temp[`${curr.dataKey}` as DataKey] = el[`${curr.dataKey}Weight`]
          return temp
        },
        {} as Record<DataKey, number>,
      )
      return {
        name: el.countryCode,
        // score: el.totalAverage as number,
        score: el.totalScore as number,
        countryName: el.countryName,
        countryLongCode: el.countryLongCode,
        countryCode: el.countryCode,
        // economy: el.economyShare as number,
        // research: el.researchShare as number,
        // economy: el.economyWeight as number, // last used
        // research: el.researchWeight as number, // last used
        ...pillarValues,
        incomeGroup: el.incomeGroup as string,
        region: el.region as string,
        metadatas: el.metadatas,
        metricsData: el.metricsData,
      }
    })
    .filter((el) => !_.isNaN(Number(el.score)))
    .sort((a, b) => b.score - a.score)
    .map((el, index) => ({
      ...el,
      id: index + 1,
    }))
}

export const generateNationalBarChartData = (
  metricGroupsParam: MetricGroup[],
  countriesData: Country[],
  configuration: {
    globalYear: number
    countryLongCode: string
    metricValue: MetricValue
  },
): BarChartNationalData => {
  const result: BarChartNationalData = []

  const { globalYear, countryLongCode } = configuration

  const country = countriesData.find((countryEl) => countryEl.countryLongCode === countryLongCode)!

  const matchingYearMetadata = country?.metadatas.find((metadata) => metadata.PublishYear === globalYear)!

  const filteredMetricGroups = filterRankingMetrics(metricGroupsParam)

  filteredMetricGroups.forEach((metricGroup) => {
    metricGroup.metrics.forEach((metric) => {
      const shortname_scaled = `${metric.shortname_scaled}_display`

      if (matchingYearMetadata?.[shortname_scaled] && matchingYearMetadata?.[shortname_scaled] !== '-') {
        result.push({
          metricName: metric.metricName
            .replace('Number of', '')
            .replace('per capita', 'PC')
            .replace('Per Capita', 'PC')
            .replace(' (Job Titles)', '')
            .replace('*', ''),
          metricNameRaw: metric.metricName,
          metricValue: metric.metricValue,
          pillar: metricGroup.metricGroupName,
          // variableName: metric.shortname_scaled as string,
          variableName: shortname_scaled as string,
          variableNameRaw: metric.shortname_raw as string,
          score: matchingYearMetadata[shortname_scaled] as number,
          countryName: country.countryName,
          id: 0,
        })
      }
    })
  })

  return (
    result
      .filter((el) => !_.isNaN(Number(el.score)))
      .filter((el) => {
        switch (configuration.metricValue) {
          case 'perCapita':
            return el.metricValue === 'perCapita'
          case 'absolute':
            return el.metricValue === 'absolute'
          default:
            return true
        }
      })
      // .sort((a, b) => b.score - a.score)
      .map((el, index) => ({
        ...el,
        id: index + 1,
      }))
  )
}

export const generateNationalRankedChartData = (
  metricGroupsParam: MetricGroup[],
  countriesParam: Country[],
  configuration: {
    globalYear: number
    metricName: string
  },
): BarChartNationalRankedData => {
  const { globalYear, metricName } = configuration
  const result: BarChartNationalRankedData = []

  const filteredMetricGroups = filterRankingMetrics(metricGroupsParam)

  const metric = filteredMetricGroups
    .reduce<MetricGroup['metrics']>((prev, cur) => [...cur.metrics, ...prev], [])
    .find((el) => el.metricName === metricName)

  if (!metric) {
    return result
  }
  countriesParam.forEach((country, index) => {
    const matchingMetricMetadata = country.metadatas.find((metadata) => metadata.PublishYear === globalYear)!
    const shortname_scaled = `${metric!.shortname_scaled}_display`
    const score = matchingMetricMetadata[shortname_scaled] as number
    result.push({
      id: index + 1,
      score,
      countryName: getCountryNameFromCountryLongCode(countriesParam, country.countryLongCode) as string,
    })
  })

  let prevScore: number | null = null
  let currentRank = 1

  let res = result
    .filter((el) => !_.isNaN(Number(el.score)))
    .sort((a, b) => b.score - a.score)
    .map((el, index) => {
      let rank
      if (prevScore === null || el.score !== prevScore) {
        currentRank = index + 1
      }
      rank = currentRank
      prevScore = el.score
      return {
        ...el,
        id: index + 1,
        cRank: rank, // Implementation of competitive ranking
      }
    })

  if (res[res.length - 1]?.cRank === res[res.length - 2]?.cRank) {
    res = res.map((i) => {
      if (i.cRank === res[res.length - 1].cRank) {
        return {
          ...i,
          cRank: res.length,
        }
      } else {
        return { ...i }
      }
    })
  }

  return res
}

export const generateSlopeChartData = (
  metricGroupParam: MetricGroup[],
  countriesParam: Country[],
  {
    yearRange,
    metricEndpointRank,
    metricEndpointWeight,
    metricValue,
    sort = 'desc',
  }: {
    yearRange: number[]
    metricEndpointWeight: string
    metricEndpointRank: string
    metricValue: MetricValue
    sort?: 'asc' | 'desc'
  },
): SlopeData => {
  return [...yearRange]
    .sort((a, b) => (sort === 'desc' ? b - a : a - b))
    .map((year) => {
      const countryYearData = calculateCountryDataFromMetricValue4SlopeChart(metricGroupParam, countriesParam, {
        globalYear: year,
        metricEndpointRank,
        metricEndpointWeight,
        metricValue,
      })
      const resultEl: ArrElement<SlopeData> = { year: year.toString() }
      countryYearData.forEach((country) => (resultEl[country.countryName] = country.ranking!))
      return resultEl
    })
}

export function shouldDisablePillar(
  data: Country[],
  metricGroup: MetricGroup,
  currentYear: number,
  metricValue: string,
) {
  if (metricValue === 'perCapita' && metricGroup?.metricGroupNameDatakey === 'diversity') {
    return true
  }
  const filteredData = metricGroup.metrics.flatMap((eachSubMetric) => {
    return data.flatMap((eachCountry) => {
      return eachCountry.metadatas.filter((i) => i.PublishYear === currentYear)[0][
        `${eachSubMetric.shortname_scaled}_display`
      ]
    })
  })
  return filteredData.every((i) => isNaN(Number(i)))
}

export function reCalculateMetricGroupsForAYear(
  metricGroupsParams: MetricGroup[],
  countryDataParams: Country[],
  metricValue: string,
  year: number,
) {
  const nextState = produce(
    { metricGroups: metricGroupsParams, countries: countryDataParams },
    ({ countries, metricGroups }) => {
      metricGroups.forEach((metricGroup) => {
        let disabledPillar = false
        disabledPillar = shouldDisablePillar(countries, metricGroup, year, metricValue)
        metricGroup.multiplier = disabledPillar ? 0 : metricGroup.multiplier
      })
    },
  )
  return nextState.metricGroups
}

export const normalizeDisabledPillars = (
  countryData: any[],
  metricGroups: any[],
  year: number,
  metricValue: string,
) => {
  if (!metricGroups?.length) return []

  return metricGroups.map((metricGroup) => {
    const isDisabled = shouldDisablePillar(countryData, metricGroup, year, metricValue)

    // If not disabled → return same values unchanged
    if (!isDisabled) {
      return {
        ...metricGroup,
        metrics: metricGroup.metrics.map((m: any) => ({ ...m })),
      }
    }

    // If disabled → zero out multipliers
    return {
      ...metricGroup,
      multiplier: 0,
      metrics: metricGroup.metrics.map((metric: any) => ({
        ...metric,
        multiplier: 0,
      })),
    }
  })
}

export const getFilteredStrengthsData = (selectedCountry: string, currentYear: number, metricValue: MetricValue) => {
  let returnData = SWData.filter((i) => i.countryCode === selectedCountry)[0]
    ?.swData.filter((j) => j.publishYear === currentYear)[0]
    ?.strengths.filter((l) => l.metricType === metricValue)

  return returnData?.length > 0 ? returnData : null
}

export const getFilteredWeaknessesData = (selectedCountry: string, currentYear: number, metricValue: MetricValue) => {
  let returnData = SWData.filter((i) => i.countryCode === selectedCountry)[0]
    ?.swData.filter((j) => j.publishYear === currentYear)[0]
    ?.weaknesses.filter((l) => l.metricType === metricValue)

  return returnData?.length > 0 ? returnData : null
}
